import React, { useState } from 'react';
import { Bookmark, Check } from 'lucide-react';
import { useVisitorFavorites } from '../hooks/useVisitorFavorites';
import { collectionName, FavoriteCollection, FavoriteImage } from '../utils/visitorFavorites';
import { useContent } from '../contexts/ContentContext';
import Modal from './Modal';

const FavoriteControls: React.FC<{ image: Pick<FavoriteImage, 'id' | 'hash' | 'year' | 'month'>; thumb?: string }> = ({ image, thumb }) => {
    const { t } = useContent();
    const { data, readable, error, change } = useVisitorFavorites();
    const saved = data.images.find(item => item.id === image.id);
    const [open, setOpen] = useState(false);
    const [selected, setSelected] = useState<string[]>([]);
    const [drafts, setDrafts] = useState<FavoriteCollection[]>([]);
    const [name, setName] = useState('');
    const [nameError, setNameError] = useState<'nameLength' | 'duplicate' | null>(null);
    const start = () => {
        setSelected(saved?.collectionIds || []); setDrafts([]); setName(''); setNameError(null); setOpen(true);
    };
    const save = () => {
        if (change(value => {
            const pending = [...drafts];
            if (name.trim()) pending.push({ id: crypto.randomUUID(), name, createdAt: Date.now() });
            for (const draft of pending) value.collections.push({ ...draft, name: collectionName(value, draft.name) });
            const ids = [...selected, ...pending.filter(item => !drafts.some(draft => draft.id === item.id)).map(item => item.id)];
            if (ids.some(id => !value.collections.some(item => item.id === id))) throw new Error('stale');
            const previous = value.images.find(item => item.id === image.id);
            value.images = value.images.filter(item => item.id !== image.id);
            value.images.unshift({ ...image, month: image.month.padStart(2, '0'), addedAt: previous?.addedAt || Date.now(), collectionIds: ids });
        })) setOpen(false);
    };
    return <div className="lab-favorite-controls">
        <button type="button" disabled={!readable} aria-pressed={!!saved} className="lab-button lab-button-primary" onClick={start}>
            {saved ? <Check aria-hidden="true" /> : <Bookmark aria-hidden="true" />}{t(saved ? 'saveDialog.saved' : 'detail.save')}
        </button>
        {!open && error && <p role="alert" className="lab-error">{error}</p>}
        <Modal helpSection="favorite-save" isOpen={open} onClose={() => setOpen(false)} title={t('saveDialog.title')} panelClassName="lab-save-dialog max-w-lg">
            <div className="lab-page p-4 space-y-4">
                {thumb && <img src={thumb} alt={t('favorites.image', { id: image.id })} className="w-24 h-24 object-contain mx-auto" />}
                <p>{t('saveDialog.unfiled')}</p>
                <fieldset className="space-y-2" disabled={!readable}>
                    <legend className="font-bold">{t('detail.collections')}</legend>
                    {[...data.collections, ...drafts].map(collection => <label key={collection.id} className="flex items-center gap-3 min-h-11 break-words">
                        <input type="checkbox" checked={selected.includes(collection.id)} onChange={event => { const checked = event.target.checked; setSelected(ids => checked ? [...ids, collection.id] : ids.filter(id => id !== collection.id)); }} />
                        <span className="min-w-0 break-all">{collection.name}</span>
                    </label>)}
                </fieldset>
                <form className="flex flex-wrap gap-2 items-end" onSubmit={event => {
                    event.preventDefault();
                    try {
                        const valid = collectionName({ ...data, collections: [...data.collections, ...drafts] }, name);
                        const draft = { id: crypto.randomUUID(), name: valid, createdAt: Date.now() };
                        setDrafts(items => [...items, draft]); setSelected(ids => [...ids, draft.id]); setName(''); setNameError(null);
                    } catch (err) { setNameError(err instanceof Error && err.message === 'duplicate' ? 'duplicate' : 'nameLength'); }
                }}>
                    <label className="flex flex-col gap-1 min-w-0 flex-1">{t('favorites.newName')}
                        <input className="border rounded-lg p-2 bg-transparent w-full" maxLength={60} value={name} onChange={event => { setName(event.target.value); setNameError(null); }} />
                    </label>
                    <button className="lab-button" type="submit">{t('favorites.create')}</button>
                </form>
                {nameError && <p className="lab-error" role="alert">{t(`favoriteErrors.${nameError}`)}</p>}
                {error && <p className="lab-error" role="alert">{error}</p>}
                <div className="lab-save-actions flex flex-wrap gap-2">
                    <button type="button" className="lab-button lab-button-primary" disabled={!readable} onClick={save}>{t(error ? 'common.retry' : 'saveDialog.commit')}</button>
                    <button type="button" className="lab-button" onClick={() => setOpen(false)}>{t('favorites.cancel')}</button>
                </div>
                {saved && <div className="border-t pt-3 space-y-2"><p>{t('saveDialog.removeHelp')}</p><button type="button" className="lab-button" onClick={() => {
                    if (change(value => { value.images = value.images.filter(item => item.id !== image.id); })) setOpen(false);
                }}>{t('detail.unsave')}</button></div>}
                <p className="lab-label">{t('detail.local')}</p>
            </div>
        </Modal>
    </div>;
};
export default FavoriteControls;
